import * as React from 'react';
import Link from 'components/common/Link';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { sendApiRequest } from 'lib/api/public';
import LogoutRequired from '../components/LogoutRequired';
import Head from 'next/head';
import Alert from '@mui/material/Alert';
import { AuthAlert, AuthContainer, authAlertSx, getErrorMessage, type AuthAlertState } from './login';

export default function ForgotPasswordForm() {
  const [loading, setLoading] = React.useState(false);
  const [isSent, setIsSent] = React.useState(false);
  const [alert, setAlert] = React.useState<AuthAlertState | null>(null);

  const emailInputRef = React.useRef<HTMLInputElement>(null);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAlert(null);

    const email = emailInputRef.current?.value;

    if (!email) {
      setAlert({ severity: 'error', message: 'An email address is required' });
      return;
    }

    setLoading(true);

    sendApiRequest({
      path: '/auth/forgot-password',
      data: {
        email,
      },
    })
      .then(() => {
        setIsSent(true);
      })
      .catch(error => {
        setAlert({ severity: 'error', message: getErrorMessage(error) });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderContent = () => {
    if (isSent) {
      return (
        <Alert severity="success" sx={authAlertSx}>
          Email sent. Please check your inbox.
          <Box sx={{ textAlign: 'right', mt: 1 }}>
            <Link href="/login">Go to Login Page</Link>
          </Box>
        </Alert>
      );
    }

    return (
      <Box>
        <AuthAlert alert={alert} />

        <Alert severity="info" sx={authAlertSx}>
          Enter your email address
          <br />
          We&apos;ll send you instructions to reset your password.
        </Alert>

        <form onSubmit={handleSubmit}>
          <TextField required fullWidth label="Email Address" inputRef={emailInputRef} autoFocus />

          <Button
            color="primary"
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2, height: '50px' }}
            disabled={loading}
          >
            Send Me Instructions
          </Button>
        </form>
      </Box>
    );
  };

  return (
    <LogoutRequired>
      <Head>
        <title>Forgot Password</title>
        <meta name="description" content="Log In" />
      </Head>

      <AuthContainer tab="forgot-password">{renderContent()}</AuthContainer>
    </LogoutRequired>
  );
}
